import type { DrawImageInstruction } from './draw-instruction';

type ImageRect = Pick<DrawImageInstruction, 'height' | 'width' | 'x' | 'y'>;

interface FitImageRectOptions extends ImageRect {
  fit: DrawImageInstruction['fit'];
  /** Intrinsic height of the source image; 0 when unknown. */
  naturalHeight: number;
  /** Intrinsic width of the source image; 0 when unknown. */
  naturalWidth: number;
}

/**
 * Where to draw an image inside its target box. `contain` keeps the image's aspect ratio
 * and centers it; `stretch`, or an image without known dimensions, fills the box.
 * Shared by the canvas and PDF renderers so the editor and the exported PDF agree.
 */
export function fitImageRect(options: FitImageRectOptions): ImageRect {
  const { fit = 'stretch', height, naturalHeight, naturalWidth, width, x, y } = options;
  if (fit === 'stretch' || naturalWidth <= 0 || naturalHeight <= 0) return { height, width, x, y };

  const scale = Math.min(width / naturalWidth, height / naturalHeight);
  const fittedWidth = naturalWidth * scale;
  const fittedHeight = naturalHeight * scale;
  return {
    height: fittedHeight,
    width: fittedWidth,
    x: x + (width - fittedWidth) / 2,
    y: y + (height - fittedHeight) / 2,
  };
}
